import { useState, useMemo } from "react";
import {
  Loader2,
  CreditCard,
  Ticket,
  ChevronDown,
  ChevronRight,
  Building2,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { EnrollmentBillingItem } from "../../api/getEnrollmentBilling/types";
import { EnrollmentBillingDrawer } from "./EnrollmentBillingDrawer";
import CollectPaymentDrawer from "./CollectPaymentDrawer";

interface TransactionsViewProps {
  enrollments: EnrollmentBillingItem[];
  isLoading: boolean;
  onRefundSuccess: () => void;
}

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

const paymentStatusLabels: Record<string, string> = {
  paid: "Paid",
  unpaid: "Unpaid",
};

const paymentStatusStyles: Record<string, string> = {
  paid: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
  unpaid: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const paymentMethodLabels: Record<string, string> = {
  card: "Card",
  voucher: "Voucher",
  total_access: "Total Access",
};

// --- Financial summary ---

function FinancialSummary({
  enrollments,
}: {
  enrollments: EnrollmentBillingItem[];
}) {
  const totals = useMemo(() => {
    let revenue = 0;
    let refunded = 0;
    let vouchers = 0;

    enrollments.forEach((item) => {
      const price = parseFloat(item.seatPrice) || 0;
      const discount = parseFloat(item.totalDiscountAmount) || 0;
      const net = price - discount;

      if (item.usedVoucher) {
        vouchers += net;
      } else if (item.refunded) {
        refunded += parseFloat(item.refundedAmount) || 0;
        revenue += net;
      } else {
        revenue += net;
      }
    });

    return {
      revenue,
      refunded,
      vouchers,
      collected: revenue - refunded,
    };
  }, [enrollments]);

  const stats = [
    { label: "Revenue", value: fmt(totals.revenue) },
    { label: "Collected", value: fmt(totals.collected) },
    { label: "Refunded", value: fmt(totals.refunded) },
    { label: "Vouchers", value: fmt(totals.vouchers) },
  ];

  return (
    <div className="flex items-center gap-6 mb-5">
      {stats.map((stat) => (
        <div className="flex items-center gap-2" key={stat.label}>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {stat.label}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {stat.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Company group ---

interface CompanyBillingGroup {
  companyName: string;
  items: EnrollmentBillingItem[];
  totalAmount: number;
  totalRefunded: number;
  totalVouchers: number;
}

function groupBillingByCompany(items: EnrollmentBillingItem[]): {
  groups: CompanyBillingGroup[];
  ungrouped: EnrollmentBillingItem[];
} {
  const map = new Map<string, EnrollmentBillingItem[]>();
  const ungrouped: EnrollmentBillingItem[] = [];

  items.forEach((item) => {
    const company = item.companyName?.trim();
    if (!company) {
      ungrouped.push(item);
      return;
    }
    if (!map.has(company)) {
      map.set(company, []);
    }
    map.get(company)!.push(item);
  });

  const groups: CompanyBillingGroup[] = [];
  map.forEach((companyItems, companyName) => {
    let totalAmount = 0;
    let totalRefunded = 0;
    let totalVouchers = 0;

    companyItems.forEach((item) => {
      const price = parseFloat(item.seatPrice) || 0;
      const discount = parseFloat(item.totalDiscountAmount) || 0;
      const net = price - discount;

      if (item.usedVoucher) {
        totalVouchers += net;
      } else {
        totalAmount += net;
        if (item.refunded) {
          totalRefunded += parseFloat(item.refundedAmount) || 0;
        }
      }
    });

    groups.push({
      companyName,
      items: companyItems,
      totalAmount,
      totalRefunded,
      totalVouchers,
    });
  });

  groups.sort((a, b) => a.companyName.localeCompare(b.companyName));
  return { groups, ungrouped };
}

// --- Billing row ---

function BillingRow({
  item,
  onSelect,
  onCollectPayment,
}: {
  item: EnrollmentBillingItem;
  onSelect: (item: EnrollmentBillingItem) => void;
  onCollectPayment: (item: EnrollmentBillingItem) => void;
}) {
  const statusLabel =
    paymentStatusLabels[item.paymentStatus] || item.paymentStatus;
  const statusStyle =
    paymentStatusStyles[item.paymentStatus] || paymentStatusStyles.unpaid;
  const methodLabel = item.paymentMethod
    ? paymentMethodLabels[item.paymentMethod]
    : null;
  const hasSyncFailure = item.sageTransactions.some(
    (txn) => txn.syncStatus === "failed",
  );

  return (
    <div
      className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 dark:border-slate-700/50 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
      onClick={() => onSelect(item)}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {item.firstName} {item.lastName}
            </p>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${statusStyle}`}
            >
              {statusLabel}
            </span>
            {methodLabel && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                {methodLabel}
              </span>
            )}
            {hasSyncFailure && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 whitespace-nowrap">
                <AlertTriangle className="h-2.5 w-2.5" />
                Sync Failed
              </span>
            )}
          </div>
          {item.enrolledAt && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {new Date(item.enrolledAt).toLocaleDateString()}
            </p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {item.paymentMethod === "total_access" ? (
              <span className="text-xs text-blue-600 dark:text-blue-400">
                Covered by Total Access subscription
              </span>
            ) : item.paymentStatus === "unpaid" ? (
              <span className="text-xs text-red-500 dark:text-red-400">
                No payment collected
              </span>
            ) : item.paymentMethod === "card" ? (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <CreditCard className="h-3 w-3" />
                {item.cardType || "Card"} ••{item.cardLast4 || "****"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Ticket className="h-3 w-3" />
                {item.voucherName || "Voucher"}
              </span>
            )}
            {item.discounts.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {item.discounts[0].code && (
                    <span className="font-mono">{item.discounts[0].code} </span>
                  )}
                  -{fmt(item.totalDiscountAmount)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        <span
          className={`text-sm font-medium ${
            item.refunded
              ? "line-through text-gray-400 dark:text-gray-500"
              : "text-gray-900 dark:text-white"
          }`}
        >
          {fmt(item.seatPrice)}
        </span>
        {item.paymentMethod === "card" &&
          parseFloat(item.remainingRefundableAmount) > 0 && (
            <Button
              className="h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item);
              }}
              size="sm"
              variant="outline"
            >
              Refund
            </Button>
          )}
        {item.paymentMethod === "voucher" && item.voucherIsActive && (
          <Button
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
            size="sm"
            variant="outline"
          >
            Return
          </Button>
        )}
        {item.paymentStatus === "unpaid" && (
          <Button
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onCollectPayment(item);
            }}
            size="sm"
            variant="default"
          >
            Collect Payment
          </Button>
        )}
      </div>
    </div>
  );
}

// --- Company billing header ---

function CompanyBillingHeader({
  group,
  isCollapsed,
  onToggle,
}: {
  group: CompanyBillingGroup;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  const summaryParts: string[] = [];
  if (group.totalAmount > 0) summaryParts.push(fmt(group.totalAmount));
  if (group.totalRefunded > 0)
    summaryParts.push(`${fmt(group.totalRefunded)} refunded`);
  if (group.totalVouchers > 0)
    summaryParts.push(`${fmt(group.totalVouchers)} voucher`);

  return (
    <button
      className="flex items-center gap-1.5 py-2 px-1 w-full text-left"
      onClick={onToggle}
      type="button"
    >
      {isCollapsed ? (
        <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      )}
      <Building2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {group.companyName}
      </span>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        ({group.items.length})
      </span>
      {summaryParts.length > 0 && (
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
          — {summaryParts.join(" · ")}
        </span>
      )}
    </button>
  );
}

// --- Main component ---

export function TransactionsView({
  enrollments,
  isLoading,
  onRefundSuccess,
}: TransactionsViewProps) {
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<EnrollmentBillingItem | null>(null);
  const [collectPaymentEnrollment, setCollectPaymentEnrollment] =
    useState<EnrollmentBillingItem | null>(null);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(
    new Set(),
  );

  const { groups, ungrouped } = useMemo(
    () => groupBillingByCompany(enrollments),
    [enrollments],
  );

  const toggleCompany = (key: string) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          No billing records found for this session.
        </p>
      </div>
    );
  }

  return (
    <>
      <FinancialSummary enrollments={enrollments} />

      <div className="space-y-1">
        {groups.map((group) => {
          const key = `billing:${group.companyName}`;
          const isCollapsed = collapsedCompanies.has(key);

          return (
            <div key={group.companyName}>
              <CompanyBillingHeader
                group={group}
                isCollapsed={isCollapsed}
                onToggle={() => toggleCompany(key)}
              />
              {!isCollapsed && (
                <div className="ml-5 border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
                  {group.items.map((item) => (
                    <BillingRow
                      item={item}
                      key={item.enrollmentId}
                      onCollectPayment={setCollectPaymentEnrollment}
                      onSelect={setSelectedEnrollment}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div className="border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
            {ungrouped.map((item) => (
              <BillingRow
                item={item}
                key={item.enrollmentId}
                onCollectPayment={setCollectPaymentEnrollment}
                onSelect={setSelectedEnrollment}
              />
            ))}
          </div>
        )}
      </div>

      <EnrollmentBillingDrawer
        enrollment={selectedEnrollment}
        onClose={() => setSelectedEnrollment(null)}
        onRefundSuccess={() => {
          setSelectedEnrollment(null);
          onRefundSuccess();
        }}
        open={!!selectedEnrollment}
      />

      <CollectPaymentDrawer
        enrollment={collectPaymentEnrollment}
        onClose={() => setCollectPaymentEnrollment(null)}
        onSuccess={() => {
          setCollectPaymentEnrollment(null);
          onRefundSuccess();
        }}
        open={!!collectPaymentEnrollment}
      />
    </>
  );
}
